import React from 'react';
import { PANTHER_THEME_STORAGE_KEY } from 'Source/constants';
import { useGetUserPreferences } from 'Source/graphql/queries/getUserPreferences.generated';
import { DEFAULT_THEME, EffectiveTheme, UserThemeOption } from './constants';

interface ThemeContextValue {
  theme: UserThemeOption;
  effectiveTheme: EffectiveTheme;
  setTheme: (theme: UserThemeOption) => void;
  /** Updates the visual theme without persisting to localStorage. Used for live preview. */
  previewTheme: (theme: UserThemeOption) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export const getSystemTheme = (): EffectiveTheme => {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

// localStorage serves as a fast cache to avoid a theme flash on page load.
// The backend (user preferences) is the source of truth for cross-device persistence.
// localStorage also enables cross-tab sync via the 'storage' event.
const getStoredTheme = (): UserThemeOption => {
  return (localStorage.getItem(PANTHER_THEME_STORAGE_KEY) as UserThemeOption) || DEFAULT_THEME;
};

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [theme, setThemeState] = React.useState<UserThemeOption>(getStoredTheme);
  const [systemPref, setSystemPref] = React.useState<EffectiveTheme>(getSystemTheme);

  // Derived — no manual sync needed across code paths
  const effectiveTheme = theme === 'system' ? systemPref : theme;

  const previewTheme = React.useCallback((newTheme: UserThemeOption) => {
    setThemeState(newTheme);
  }, []);

  const setTheme = React.useCallback((newTheme: UserThemeOption) => {
    setThemeState(newTheme);
    localStorage.setItem(PANTHER_THEME_STORAGE_KEY, newTheme);
  }, []);

  // Sync theme from backend on app load so cross-device persistence works
  // without requiring a visit to the Settings page.
  const { data: prefsData } = useGetUserPreferences({ fetchPolicy: 'cache-first' });
  React.useEffect(() => {
    const backendTheme = prefsData?.userPreferences?.theme as UserThemeOption | null;
    if (backendTheme) {
      setTheme(backendTheme);
    }
  }, [prefsData?.userPreferences?.theme, setTheme]);

  // Listen for system theme changes when user selects "system" theme
  React.useEffect(() => {
    if (theme !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      setSystemPref(e.matches ? 'light' : 'dark');
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme]);

  // Sync theme changes across browser tabs via localStorage 'storage' event
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PANTHER_THEME_STORAGE_KEY && e.newValue) {
        setThemeState(e.newValue as UserThemeOption);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = React.useMemo(
    () => ({ theme, effectiveTheme, setTheme, previewTheme }),
    [theme, effectiveTheme, setTheme, previewTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export { DEFAULT_THEME };
